import { useCallback, useEffect, useRef } from 'react';
import { AbortError } from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import type { AnyVariables, DocumentInput, OperationContext } from 'urql';
import { CombinedError, useClient } from 'urql';
import { filter, onEnd, pipe, subscribe, take } from 'wonka';

export const NO_DATA_RETURNED = 'Query returned no data';

interface CancellableQueryOptions {
  allowPartialData?: boolean;
}

/**
 * Runs a GraphQL query imperatively with automatic cancellation.
 *
 * Use this only when the result feeds a decision (navigate, submit, poll, chain
 * another request). If the component renders the data, use `useQuery` instead.
 *
 * - Resolves with the query `data`.
 * - Rejects with `CombinedError` when the result has a GraphQL or network error,
 *   or when no data is returned (e.g. a `cache-only` miss, or the source ends
 *   without a final result).
 * - With `allowPartialData`, resolves with `data` when the result has both data
 *   and an error. Opt in only when the caller can act safely on partial data.
 * - Rejects with `AbortError` when cancelled by unmount, a new execution, or
 *   `abortCurrentQuery`.
 * - Defaults `requestPolicy` to `network-only`; pass `context` to override it.
 *
 * @param query - GraphQL query document
 * @param options - `allowPartialData` resolves partial results instead of rejecting
 * @returns Tuple of [executeQuery, abortCurrentQuery]
 *
 * @example
 * ```ts
 * const [executeQuery] = useCancellableQuery<Query>(GetProfileInfo);
 *
 * const handleContinue = async () => {
 *   try {
 *     const data = await executeQuery({});
 *     if (data.user?.isEligible) {
 *       router.push('/next-step');
 *     }
 *   } catch (error) {
 *     if (error instanceof AbortError) {
 *       return;
 *     }
 *     // Handle CombinedError
 *   }
 * };
 * ```
 */
export function useCancellableQuery<
  Data = Record<string, unknown>,
  Variables extends AnyVariables = AnyVariables,
>(
  query: DocumentInput<Data, Variables>,
  { allowPartialData = false }: CancellableQueryOptions = {},
) {
  const client = useClient();
  const cancelCurrentQueryRef = useRef<(() => void) | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelCurrentQueryRef.current?.();
    };
  }, []);

  const abortCurrentQuery = useCallback(() => {
    const cancelCurrentQuery = cancelCurrentQueryRef.current;
    cancelCurrentQueryRef.current = null;
    cancelCurrentQuery?.();
  }, []);

  const executeQuery = useCallback(
    (variables: Variables, context?: Partial<OperationContext>) => {
      abortCurrentQuery();

      return new Promise<Data>((resolve, reject) => {
        let isSettled = false;

        const settle = () => {
          isSettled = true;

          if (cancelCurrentQueryRef.current === cancelQuery) {
            cancelCurrentQueryRef.current = null;
          }
        };

        const { unsubscribe } = pipe(
          client.query(query, variables, {
            requestPolicy: 'network-only',
            ...context,
          }),
          filter((result) => !result.stale && !result.hasNext),
          take(1),
          onEnd(() => {
            // The source ended without emitting a final (non-stale) result.
            if (!isSettled) {
              settle();
              reject(new CombinedError({ graphQLErrors: [NO_DATA_RETURNED] }));
            }
          }),
          subscribe((result) => {
            settle();

            const { data, error } = result;

            if (error && !(allowPartialData && data !== undefined)) {
              reject(error);
            } else if (data === undefined) {
              reject(new CombinedError({ graphQLErrors: [NO_DATA_RETURNED] }));
            } else {
              resolve(data);
            }
          }),
        );

        function cancelQuery() {
          // Settle first so the teardown's onEnd doesn't reject with CombinedError.
          isSettled = true;
          unsubscribe();
          reject(new AbortError());
        }

        // Results served synchronously from the cache settle before subscribe returns.
        if (!isSettled) {
          cancelCurrentQueryRef.current = cancelQuery;
        }
      });
    },
    [abortCurrentQuery, allowPartialData, client, query],
  );

  return [executeQuery, abortCurrentQuery] as const;
}

export default useCancellableQuery;
